import { Link } from "react-router-dom";
import type { Match } from "@/types";
import { teamMap } from "@/mocks/teams";
import TeamLogo from "@/components/base/TeamLogo";
import StatusBadge from "@/components/base/StatusBadge";
import { formatTime, formatDateLabel } from "@/utils/format";

interface MatchCardProps {
  match: Match;
}

export default function MatchCard({ match }: MatchCardProps) {
  const home = teamMap[match.homeTeamId];
  const away = teamMap[match.awayTeamId];
  if (!home || !away) return null;

  const isLive = match.status === "live";
  const hasScore = match.status !== "upcoming";

  return (
    <div
      className="group rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 transition-all hover:border-primary-300 hover:bg-background-100"
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <StatusBadge status={match.status} period={match.status === "live" ? match.period : undefined} />
        <span className="text-xs text-foreground-500 whitespace-nowrap">
          {formatDateLabel(match.startTime)} {formatTime(match.startTime)}
        </span>
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
          <TeamLogo team={home} />
          <span className="text-sm font-semibold text-foreground-950 text-center truncate w-full">{home.name}</span>
        </div>

        <div className="flex flex-col items-center px-2 shrink-0">
          {hasScore ? (
            <div className="flex items-center gap-2 font-heading font-bold text-2xl md:text-3xl">
              <span className={isLive ? "text-accent-600" : "text-foreground-950"}>{match.homeScore}</span>
              <span className="text-foreground-300 text-lg">:</span>
              <span className={isLive ? "text-accent-600" : "text-foreground-950"}>{match.awayScore}</span>
            </div>
          ) : (
            <span className="font-heading font-bold text-xl text-foreground-400">VS</span>
          )}
          <span className="mt-1 text-[11px] text-foreground-500 whitespace-nowrap">{match.stage}</span>
        </div>

        <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
          <TeamLogo team={away} />
          <span className="text-sm font-semibold text-foreground-950 text-center truncate w-full">{away.name}</span>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
        <span className="flex items-center gap-1 text-xs text-foreground-500 min-w-0 truncate">
          <i className="ri-map-pin-line" />
          <span className="truncate">{match.venue}</span>
        </span>
        <Link
          to={`/match/${match.id}`}
          className="inline-flex items-center gap-1 rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
        >
          <i className={isLive ? "ri-live-line" : "ri-play-circle-line"} />
          {isLive ? "观看直播" : match.status === "replay" ? "观看回放" : "赛事详情"}
        </Link>
      </div>
    </div>
  );
}